import React, { useEffect, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import { formatI18nKeys, toI18N } from "../../i18n";
import { useCooldown } from "../../hooks/useCooldown";
import { Loader, useWindowSize, Wrapper } from "../common";
import CommunityBrandHeader from "./CommunityBrandHeader";
import ProviderHint from "./ProviderHint";
import { authErrorMessage } from "../../utils/authErrors";

export type EmailScreenProps = {
	email: string;
	useCodeVerification: boolean;
	loading: boolean;
	debugEmailToken?: string;
	coverImage?: string;
	backgroundColor?: string;
	logo?: string;
	title?: string;
	codeError?: string;
	onClearCodeError?: () => void;
	handleVerificationCode: (code: string) => void;
	handleBack: () => void;
	verifyLockSeconds: number;
	verifyLockNonce: number;
	onResendCode: () => Promise<{ token?: string; time?: number; backoff?: number }>;
};

// Validate verification code format (6 digits only)
const isValidVerificationCode = (code: string): boolean => {
	return /^\d{6}$/.test(code);
};

export default function EmailScreen(props?: EmailScreenProps) {
	const [verificationCode, setVerificationCode] = useState<string>(
		props?.debugEmailToken || "",
	);
	const [inIframe, setInIframe] = useState(false);
	const size = useWindowSize();
	const verifyLock = useCooldown(0);
	const resend = useCooldown(30);
	const [resendLoading, setResendLoading] = useState(false);
	const [resendError, setResendError] = useState("");

	// Wrong-code and resend failures render inline under the input (persistent,
	// assertive) to match the age screen's error pattern — not as toasts.
	const inlineError = props.codeError || resendError;

	const handleResend = async () => {
		if (resend.active || resendLoading) return;
		setResendLoading(true);
		setResendError("");
		try {
			const data = await props.onResendCode();
			if (data?.token) {
				toast.success(toI18N("lp_login_otp_resend_success_toast"));
				resend.start(Number(data?.backoff ?? data?.time) || 30);
			} else {
				resend.start(Number(data?.backoff ?? data?.time) || 30);
			}
		} catch (e) {
			setResendError(authErrorMessage(e));
		} finally {
			setResendLoading(false);
		}
	};

	useEffect(() => {
		setInIframe(window.top !== window.self);
	}, []);

	useEffect(() => {
		if (props.verifyLockSeconds > 0) verifyLock.start(props.verifyLockSeconds);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.verifyLockNonce]);

	const handleKeyDown = (e: any) => {
		if (
			e.key === "Enter" &&
			!verifyLock.active &&
			isValidVerificationCode(verificationCode)
		) {
			props.handleVerificationCode(verificationCode);
		}
	};

	return (
		<Wrapper
			InIframe={inIframe}
			isMobile={size.width < 481}
			coverImage={props?.coverImage}
			backgroundColor={props?.backgroundColor}
		>
			<div className="p-8" aria-busy={props.loading}>
				{/* Nav row: back control left, community lockup centered
				    (symmetric flex-1 spacers keep it optically centered). */}
				<div className="mb-4 flex items-center">
					<div className="flex flex-1 justify-start">
						<button
							onClick={props.handleBack}
							className="p-2 bg-neutral-1 rounded-full hover:bg-neutral-11 text-primary-3"
							aria-label="Go back"
						>
							<img
								src={"/back_button.svg"}
								alt=""
								aria-hidden="true"
								className={`w-15 h-15`}
							/>
						</button>
					</div>
					<CommunityBrandHeader logo={props.logo} title={props.title} />
					<div className="flex-1" aria-hidden="true" />
				</div>
				<div className="flex flex-col items-center">
					<img
						src={"/logo_email.svg"}
						alt="Email sent"
						className={`mb-3 w-20 h-20`}
					/>
					<h1
						className="mb-5 font-bold text-center"
						style={{
							fontSize: "24px",
							fontWeight: "700",
							lineHeight: "29px",
							letterSpacing: "0em",
						}}
					>
						{toI18N("lp_login_signup_email_check_inbox")}
					</h1>
					<h2
						className="mb-5 pb-2"
						style={{
							fontSize: "16px",
							fontWeight: "300",
							lineHeight: "22px",
							letterSpacing: "0em",
							textAlign: "center",
						}}
					>
						{props.useCodeVerification
							? formatI18nKeys("lp_login_signup_verification_code")
							: formatI18nKeys(
									"lp_login_signup_verification_link",
									props.email,
								)}
						{props.useCodeVerification && (
							<>
								<br />
								<span style={{ fontWeight: "600" }}>{props.email}</span>
							</>
						)}
					</h2>
					{props.useCodeVerification && (
						<>
							{verifyLock.active && (
								<div className="mb-3 w-full rounded-lg border border-secondary-1/30 bg-secondary-1/10 px-4 py-2 text-center text-sm text-secondary-1">
									{formatI18nKeys(
										"lp_login_otp_backoff_notice",
										String(verifyLock.seconds),
									)}
								</div>
							)}
							<label htmlFor="verification-code" className="sr-only">
								{toI18N("lp_login_email_code_label")}
							</label>
							<input
								id="verification-code"
								type="text"
								inputMode="numeric"
								pattern="[0-9]*"
								maxLength={6}
								placeholder="------"
								className={`mb-3 w-full border p-3 rounded bg-primary-4 text-center text-lg tracking-[0.3em] ${
									inlineError ? "border-red-500" : "border-neutral-11"
								}`}
								style={{ borderRadius: "8px" }}
								onKeyDown={handleKeyDown}
								onChange={(e) => {
									// Only allow numeric input
									const value = e.target.value.replace(/\D/g, "").slice(0, 6);
									setVerificationCode(value);
									if (resendError) setResendError("");
									props.onClearCodeError?.();
								}}
								value={verificationCode}
								autoFocus={true}
								autoComplete="one-time-code"
								disabled={verifyLock.active}
								aria-describedby={
									inlineError ? "verification-code-error" : undefined
								}
								aria-invalid={inlineError ? "true" : undefined}
							/>
							{inlineError && (
								<p
									id="verification-code-error"
									className="mb-3 -mt-1 w-full text-sm text-red-600"
									role="alert"
								>
									{inlineError}
								</p>
							)}
							<button
								onClick={() => {
									if (
										!verifyLock.active &&
										isValidVerificationCode(verificationCode)
									) {
										props.handleVerificationCode(verificationCode);
									}
								}}
								disabled={verifyLock.active || !isValidVerificationCode(verificationCode)}
								className={`w-full mb-3 p-3 border border-neutral-11 text-neutral-1 ${
									!verifyLock.active && isValidVerificationCode(verificationCode)
										? "bg-primary-1 hover:bg-primary-2"
										: "bg-neutral-7 cursor-not-allowed"
								}`}
								style={{
									borderRadius: "24px",
									fontSize: "16px",
									fontWeight: "700",
									lineHeight: "18px",
									letterSpacing: "0em",
									textAlign: "center",
								}}
							>
								{toI18N("login_otp_btn")}
							</button>
							<ProviderHint email={props.email} />
							<div className="mt-4 text-center text-sm">
								{resend.active ? (
									<span className="text-neutral-10">
										{toI18N("login_otp_resend_after")} {resend.seconds}s
									</span>
								) : (
									<button
										type="button"
										onClick={handleResend}
										disabled={resendLoading}
										className="font-bold text-primary-2"
									>
										{toI18N("login_otp_resend_label")}
									</button>
								)}
							</div>
						</>
					)}
					{!props.useCodeVerification && (
						<ProviderHint email={props.email} />
					)}
				</div>
				<Toaster position="bottom-center" />
				{props.loading && (
					<div className="fixed inset-0 z-50">
						<div className="absolute inset-0 bg-primary-4 opacity-50" />
						<div className="absolute inset-0">
							<Loader />
						</div>
					</div>
				)}
			</div>
		</Wrapper>
	);
}
